// Time-zone helpers.
//
//   • Data is stored as CENTRAL TIME ("13:00" in the DB means 1:00 PM CST/CDT).
//   • The Gabruz Universe community is based in Pittsburgh, so the UI
//     displays everything in EASTERN TIME (CST + 1 hour, CDT + 1 hour).
//
// Use:
//   fmtMatchTime("13:00")          → "2:00 PM"   (CST → EST shift)
//   centralToMs("2026-06-28", ...) → ms timestamp (countdown math)
//   fmtTimeEastern(date)           → "2:00 PM"   (always Eastern)
//   fmtDateEastern(date)           → "Sun, Jun 28"

const TZ = "America/New_York";

// Format a Date (or Date-able value) as "1:30 PM" in Eastern Time.
export function fmtTimeEastern(d) {
  if (d == null) return "";
  const date = d instanceof Date ? d : new Date(d);
  if (!Number.isFinite(date.getTime())) return "";
  return new Intl.DateTimeFormat("en-US", {
    timeZone: TZ,
    hour: "numeric",
    minute: "2-digit"
  }).format(date);
}

// Format a Date as "Sun, Jun 28" in Eastern Time.
export function fmtDateEastern(d) {
  if (d == null) return "";
  const date = d instanceof Date ? d : new Date(d);
  if (!Number.isFinite(date.getTime())) return "";
  return new Intl.DateTimeFormat("en-US", {
    timeZone: TZ,
    weekday: "short",
    month: "short",
    day: "numeric"
  }).format(date);
}

// Format an HH:MM string stored as CENTRAL TIME ("13:00" = 1 PM CST)
// as the equivalent Eastern wall-clock time. Eastern is always
// exactly one hour ahead of Central regardless of DST status,
// so we just add 1 hour and reformat as 12-hour AM/PM.
//
// fmtMatchTime("13:00") → "2:00 PM"
// fmtMatchTime("23:30") → "12:30 AM"   (wraps past midnight)
export function fmtMatchTime(hhmm) {
  if (!hhmm) return "";
  const m = String(hhmm).match(/^(\d{1,2}):(\d{2})/);
  if (!m) return hhmm;
  let h = Number(m[1]);
  const min = Number(m[2]);
  if (!Number.isFinite(h) || !Number.isFinite(min)) return hhmm;
  // Central → Eastern = +1 hour. Wrap past midnight.
  h = (h + 1) % 24;
  const d = new Date(2026, 5, 1, h, min, 0);
  return new Intl.DateTimeFormat("en-US", {
    hour: "numeric",
    minute: "2-digit"
  }).format(d);
}

// Approximate millisecond timestamp for a match-date + match-time
// pair stored as CENTRAL TIME. Used by countdown math so the
// "kickoff in X" widget works correctly for users browsing from
// any timezone.
//
// DST heuristic: Mar–Oct mostly CDT (UTC-5), Nov–Feb mostly CST
// (UTC-6). Off by ~3-4 days a year around the DST transitions;
// good enough for a community sports app.
export function centralToMs(dateIso, timeHhmm) {
  if (!dateIso || !timeHhmm) return NaN;
  const m = parseInt(dateIso.slice(5, 7), 10);
  const offset = (m >= 3 && m <= 10) ? "-05:00" : "-06:00";
  const t = new Date(`${dateIso}T${timeHhmm}:00${offset}`).getTime();
  return Number.isFinite(t) ? t : NaN;
}

// Back-compat alias — older callers expected this name. New code
// should use `centralToMs` so the source-of-truth timezone is clear.
export const easternToMs = centralToMs;
